import React, { FC, useRef, useEffect } from 'react';
import ReactModal from 'react-modal';
import classnames from 'classnames';

import {
  ReservationFlowMe,
  ReservationFlowProductDetail,
  ReservationFlowCustomerSubscriptionDetail,
  ReservationFlowInvalidRequestError,
  ReservationFlowResourceSchemeType,
} from 'src/interfaces/reservation_flow';
import {
  ProductPurchaseServiceContext,
  ProductPurchaseServiceContextProvider,
} from 'src/contexts/state-machines/purchase/ProductPurchaseServiceContext';
import { useIsMobile } from 'src/reservation-page/hooks/helpers/useIsMobile';
import { useReactModalAnimationWorkaround } from 'src/reservation-page/hooks/helpers/useReactModalAnimationWorkaround';

import { ViewHandler } from './components/ViewHandler';

import styles from './index.module.css';
import { useGetReservationFlowProduct } from 'src/hooks/api/reservation-flow/useGetReservationFlowProduct';
import { ReserveStripeElements } from 'src/components/stripe/ReserveStripeElements';

export interface SubscriptionPurchaseModalProps {
  isOpen: boolean;
  onRequireClosing: () => void;
  onFinish: (params: {
    productPublicId: number;
    productDetail: ReservationFlowProductDetail;
    submitResult: ReservationFlowCustomerSubscriptionDetail;
  }) => void;
  onError: (error: ReservationFlowInvalidRequestError) => void;
  merchantPublicId: string;
  user: ReservationFlowMe;
  isJCBEnabled: boolean;
  resourceSchemeType: ReservationFlowResourceSchemeType;
  productPublicId?: number;
  sellerExternalId?: string;
}

export const SubscriptionPurchaseModal: FC<SubscriptionPurchaseModalProps> = (
  props,
) => {
  return (
    <ProductPurchaseServiceContextProvider>
      <Internal {...props} />
    </ProductPurchaseServiceContextProvider>
  );
};

const Internal: FC<SubscriptionPurchaseModalProps> = ({
  isOpen,
  onRequireClosing,
  onFinish,
  onError,
  merchantPublicId,
  user,
  isJCBEnabled,
  resourceSchemeType,
  productPublicId,
  sellerExternalId,
}) => {
  const isMobile = useIsMobile();

  const { afterOpenModal, afterClose, wasOpen } =
    useReactModalAnimationWorkaround(false);

  const [state, send] = ProductPurchaseServiceContext.useActor();

  const isSubmitting = state.matches('confirming.subscriptionSubmitting');

  useEffect(() => {
    if (typeof productPublicId === 'undefined') return;

    send({
      type: 'SET_PRODUCT_PUBLIC_ID',
      payload: { productPublicId },
    });
  }, [productPublicId, send]);

  const isCallbackCalledRef = useRef(false);
  useEffect(() => {
    if (isCallbackCalledRef.current) return;

    switch (true) {
      case state.matches('complete'):
        onFinish({
          productPublicId: state.context.data.productPublicId!,
          productDetail: state.context.data.productDetail!,
          submitResult: state.context.data.customerSubscriptionSubmitResult!,
        });
        isCallbackCalledRef.current = true;
        break;
      case state.matches('error'):
        onError(state.context.data.submitError!);
        isCallbackCalledRef.current = true;
        break;
      default:
        break;
    }
  }, [onFinish, onError, state]);

  useEffect(() => {
    if (isOpen) {
      send({
        type: 'SET_MERCHANT_PUBLIC_ID',
        payload: { merchantPublicId },
      });
      send({
        type: 'SET_IS_JCB_ENABLED',
        payload: { isJCBEnabled },
      });
      send({
        type: 'SET_RESOURCE_SCHEME_TYPE',
        payload: { resourceSchemeType },
      });
      return;
    }

    send({
      type: 'RESET_DATA',
    });
    send({
      type: 'GOTO_INITIALIZING',
    });
    isCallbackCalledRef.current = false;
  }, [isOpen, merchantPublicId, isJCBEnabled, send, resourceSchemeType]);

  const { data: productDetail, error: productDetailError } =
    useGetReservationFlowProduct({
      parameter:
        typeof productPublicId !== 'undefined'
          ? {
              merchant_public_id: merchantPublicId,
              product_public_id: productPublicId,
            }
          : undefined,
      isDisabled: typeof productPublicId === 'undefined',
    });

  useEffect(() => {
    if (productDetail) {
      send({
        type: 'SET_PRODUCT_DETAIL',
        payload: { productDetail },
      });
      return;
    }

    if (!productDetailError) return;

    send({
      type: 'SET_IS_REQUIRE_VERIFYING',
    });
  }, [productDetail, productDetailError, send]);

  const stripeProps = state.context.data.productDetail?.is_wait_first_anchor
    ? ({
        mode: 'setup',
      } as const)
    : ({
        mode: 'payment',
        // 月謝は常に保存する
        savePaymentMethod: true,
        amount: state.context.data.productDetail?.price,
        sellerId:
          productDetail && !productDetail.is_wait_first_anchor
            ? sellerExternalId
            : undefined,
      } as const);

  return (
    <ReactModal
      appElement={
        document.getElementById('__next') !== null
          ? document.getElementById('__next')!
          : undefined
      }
      className={{
        base: classnames(
          isMobile ? 'w-full' : 'max-w-[560px]',
          isMobile ? 'rounded-t-20' : 'rounded-15',
          isMobile ? 'mt-2' : 'mt-[80px]',
          isMobile && 'bottom-0',
          !isMobile && 'mx-auto',
          'bg-white',
          'overflow-hidden',
          isMobile ? styles.spContentBase : styles.contentBase,
        ),
        afterOpen: classnames(
          wasOpen && styles.contentBaseAfterOpen,
          isMobile && styles.spContentAfterOpen,
        ),
        beforeClose: classnames(
          styles.contentBaseBeforeClose,
          isMobile && styles.spContentBeforeClose,
        ),
      }}
      overlayClassName={{
        base: classnames(
          'fixed',
          'z-8',
          !isMobile && 'px-4',
          styles.overlayBase,
        ),
        afterOpen: classnames(wasOpen && styles.overlayBaseAfterOpen),
        beforeClose: styles.overlayBaseBeforeClose,
      }}
      closeTimeoutMS={150}
      onRequestClose={!isSubmitting ? onRequireClosing : undefined}
      onAfterOpen={afterOpenModal}
      onAfterClose={afterClose}
      isOpen={isOpen}
      shouldFocusAfterRender={false}
      bodyOpenClassName={styles.bodyOpen}
    >
      <ReserveStripeElements {...stripeProps}>
        <ViewHandler
          onRequireClosing={onRequireClosing}
          merchantPublicId={merchantPublicId}
          user={user}
          productPublicId={productPublicId}
        />
      </ReserveStripeElements>
    </ReactModal>
  );
};
